import React, { useCallback, useMemo } from "react";
import { Route } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { useDispatch } from "react-redux";
import { Box, Button, Container } from "@mui/material";

import { FullScreenLoader } from "./full-screen-loader";
import { userPreferredLguage } from "../actions/Auth";
import IntlMessages from "util/IntlMessages";
import { LogoHeaderCard } from "./logo-header-card";
import { initiateLogout } from "util/auth-utils";
import { useAuthGate } from "hooks/use-auth-gate";

function ProtectedRoute({
  component: Component,
  path,
  remoteTranslations,
  isInitialTranslationsLoadComplete,
  ...rest
}) {
  const dispatch = useDispatch();
  const { errorMessage, isLoading } = useAuthGate({
    onValidateUser: (userData) => {
      if (
        userData?.user &&
        Object.keys(remoteTranslations).length > 0 &&
        Object.keys(userData.user).length > 0
      ) {
        dispatch(userPreferredLguage(userData.user));
      }
    },
  });
  const auth = useAuth();

  const hasRemoteTranslations = useMemo(
    () => Object.keys(remoteTranslations).length > 0,
    [remoteTranslations]
  );
  const isPendingTranslations =
    !isInitialTranslationsLoadComplete || !hasRemoteTranslations;

  const handleLogin = useCallback(() => {
    if (auth.isAuthenticated) {
      initiateLogout();
    } else {
      auth.signinRedirect();
    }
  }, [auth]);

  const renderRoute = useCallback(
    (props) => {
      if (errorMessage && !isPendingTranslations) {
        return (
          <Container
            maxWidth="sm"
            sx={{
              width: "100%",
              height: "100vh",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <LogoHeaderCard
              title={<IntlMessages id="DIC_LOGIN_ERROR_TITLE" />}
              subtitle={errorMessage}
            >
              <Box display="flex" justifyContent="center">
                <Button variant="contained" onClick={handleLogin}>
                  <IntlMessages id="DIC_LOGIN_TRY_AGAIN_BUTTON" />
                </Button>
              </Box>
            </LogoHeaderCard>
          </Container>
        );
      }

      if (isLoading || isPendingTranslations) {
        return <FullScreenLoader />;
      }

      return <Component {...props} />;
    },
    [errorMessage, handleLogin, isPendingTranslations, isLoading]
  );

  return <Route {...rest} render={renderRoute} />;
}

export default ProtectedRoute;
